import { useState } from "react";
import { Button } from "@mui/material";
import { PersonAdd } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import { Panel } from "@poseidon/ui/components/Panel";
import RouterLink from "@poseidon/ui/components/RouterLink";
import { CurrencyCell } from "@poseidon/ui/components/table/CurrencyCell";
import { SailorCell } from "@poseidon/ui/components/table/SailorCell";
import { WithDoc } from "@poseidon/firebase/components/WithDoc";
import { getDate } from "@poseidon/firebase/timestamp";
import { RateTypeChip } from "./RateTypeChip";
import { usePermissions } from "@poseidon/ui/components";
import { AddAgreementSailorDialog } from "../jointSailor/AddAgreementSailorDialog";

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "& > *:first-of-type": {
    paddingLeft: theme.spacing(3),
  },
}));
export const AgreementSummary = ({ agreement }) => {
  const { canWrite } = usePermissions();
  const [addingJoint, setAddingJoint] = useState(false);
  const sailorIds = (agreement.sailors ?? []).map((ref) => ref.id);
  return (
    <Panel title="Summary" expandable={true} nopadding={true}>
      <Table>
        <TableBody>
          <StyledTableRow>
            <TableCell component="th" scope="row" width={160}>
              Sailor
            </TableCell>
            <SailorCell
              sailorRef={agreement.sailors && agreement.sailors[0]}
              applicationRef={agreement.application}
            />
          </StyledTableRow>
          {agreement.sailors?.length > 1 ? (
            <StyledTableRow>
              <TableCell component="th" scope="row" width={160}>
                Joint Sailor
              </TableCell>
              <SailorCell
                sailorRef={agreement.sailors && agreement.sailors[1]}
                applicationRef={agreement.application}
              />
            </StyledTableRow>
          ) : (
            canWrite && (
              <StyledTableRow>
                <TableCell component="th" scope="row" width={160}>
                  Joint Sailor
                </TableCell>
                <TableCell>
                  <Button
                    variant="outlined"
                    startIcon={<PersonAdd />}
                    onClick={() => setAddingJoint(true)}
                  >
                    Add joint sailor
                  </Button>
                </TableCell>
              </StyledTableRow>
            )
          )}
          <StyledTableRow>
            <TableCell component="th" scope="row">
              Vessel
            </TableCell>
            <TableCell>
              <WithDoc
                docRef={agreement.vessel}
                Component={({ data: boat }) => (
                  <RouterLink to={`/boats/${boat.id}`}>
                    {boat.make} - {boat.model}
                  </RouterLink>
                )}
              />
            </TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <TableCell component="th" scope="row">
              Cost of Vessel
            </TableCell>
            <CurrencyCell value={agreement.parameters.costOfVessel} />
          </StyledTableRow>
          <StyledTableRow>
            <TableCell component="th" scope="row">
              Deposit
            </TableCell>
            <CurrencyCell value={agreement.parameters.deposit} />
          </StyledTableRow>
          <StyledTableRow>
            <TableCell component="th" scope="row">
              Term
            </TableCell>
            <TableCell>{agreement.parameters.termMonths} months</TableCell>
          </StyledTableRow>
          {agreement.parameters.balloon > 0 && (
            <StyledTableRow>
              <TableCell component="th" scope="row">
                Balloon
              </TableCell>
              <CurrencyCell value={agreement.parameters.balloon} />
            </StyledTableRow>
          )}
          <StyledTableRow>
            <TableCell component="th" scope="row">
              Rate Type
            </TableCell>
            <TableCell>
              <RateTypeChip fixedRate={agreement.parameters.fixedRate} />
            </TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <TableCell component="th" scope="row">
              Capital Balance
            </TableCell>
            <CurrencyCell value={-agreement.capitalBalance} />
          </StyledTableRow>
          <StyledTableRow>
            <TableCell component="th" scope="row">
              Arrears
            </TableCell>
            <CurrencyCell value={agreement.arrears} />
          </StyledTableRow>
          <StyledTableRow>
            <TableCell component="th" scope="row">
              Arrears since
            </TableCell>
            <TableCell>
              {getDate(
                agreement.dateOfOldestDuePayment
              )?.toLocaleDateString() ?? "N/A"}
            </TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <TableCell component="th" scope="row">
              Due Payments
            </TableCell>
            <TableCell>{agreement.duePayments ?? 0}</TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <TableCell component="th" scope="row">
              Agreement Creation
            </TableCell>
            <TableCell>
              {getDate(agreement.agreementCreation)?.toLocaleString() ?? 0}
            </TableCell>
          </StyledTableRow>
        </TableBody>
      </Table>
      <AddAgreementSailorDialog
        open={addingJoint}
        onClose={() => setAddingJoint(false)}
        agreementId={agreement.id}
        excludeSailorIds={sailorIds}
      />
    </Panel>
  );
};
